import React from "react";
import { Box, Typography } from "@mui/material";

import { useSpreadsheetParser } from "./DataImport/useSpreadsheetParser";
import { useImportState } from "./DataImport/useImportState";
import { useImportActions } from "./DataImport/useImportActions";

import DataImportStepperBar from "./DataImport/DataImportStepperBar";
import { StepUpload } from "./DataImport/StepUpload";
import { StepMapping } from "./DataImport/StepMapping";
import { StepValidation } from "./DataImport/StepValidation";
import { StepFinalImport } from "./DataImport/StepFinalImport";
import { StepVerifyAddresses } from "./DataImport/StepVerifyAddresses";

interface DataImportPageProps {
  title: string;
  modelFields: any;
  importFunction: (
    formData: FormData,
    action: "validate" | "resolve_addresses" | "import"
  ) => Promise<any>;
}

const hasUnresolvedAddresses = (
  resolution: any,
  overrides: Record<string, any>
) => {
  if (!resolution?.rows) return false;

  return resolution.rows.some((r: any) => {
    const hasAnyInput = Object.values(r.raw_parts || {}).some(
      (v: any) => String(v || "").trim() !== ""
    );
    if (!hasAnyInput) return false;

    const rowKey = String(r.row);
    return !overrides?.[rowKey];
  });
};

export const DataImportPage: React.FC<DataImportPageProps> = ({
  title,
  modelFields,
  importFunction,
}) => {
  const steps = [
    "Upload & Preview",
    "Column Mapping",
    "Validate Data",
    "Verify Addresses",
    "Confirm Import",
  ];

  const { data, columns, loading, parseFile } = useSpreadsheetParser();

  const state = useImportState();
  const { file, setFile, activeStep, setActiveStep } = state;

  const [addressResolution, setAddressResolution] = React.useState<any>(null);
  const [addressOverrides, setAddressOverrides] = React.useState<
    Record<string, any>
  >({});

  const [finished, setFinished] = React.useState(false);
  const [showNextLoading, setShowNextLoading] = React.useState(false);

  const { validateFile, resolveAddresses, importFile } = useImportActions(
    async (
      formData: FormData,
      action: "validate" | "resolve_addresses" | "import"
    ) => importFunction(formData, action),
    state.invertMapping,
    state.file,
    state.setUploading,
    state.setValidation,
    state.setImportResult,
    setAddressResolution,
    () => addressOverrides
  );

  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (state.uploading) {
      timer = setTimeout(() => {
        setShowNextLoading(true);
      }, 2000);
    } else {
      setShowNextLoading(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [state.uploading]);

  const handleFileSelect = async (f: File) => {
    setFinished(false);
    setAddressResolution(null);
    setAddressOverrides({});

    setFile(f);
    state.updateMapping("", "");
    await parseFile(f);
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      const result = await validateFile();
      if (result) setActiveStep(2);
      return;
    }

    if (activeStep === 2) {
      if (state.validation?.invalid_rows === 0) {
        await resolveAddresses();
        setActiveStep(3);
      }
      return;
    }

    if (activeStep === 3) {
      setActiveStep(4);
      return;
    }

    if (activeStep === 4) {
      if (finished) return;

      setFinished(true);
      await importFile();
      return;
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box sx={{ width: "100%", maxWidth: "1400px" }}>
        <DataImportStepperBar
          steps={steps}
          activeStep={activeStep}
          onBack={() => setActiveStep(activeStep - 1)}
          onNext={handleNext}
          showNextLoading={showNextLoading}
          disableNext={
            finished ||
            (activeStep === 0 && columns.length === 0) ||
            (activeStep === 2 && state.validation?.invalid_rows > 0) ||
            (activeStep === 3 &&
              hasUnresolvedAddresses(addressResolution, addressOverrides))
          }
        />

        <Box sx={{ mt: 4 }}>
          {activeStep === 0 && (
            <Box sx={{ overflowX: "auto" }}>
              <StepUpload
                file={file}
                onFileSelect={handleFileSelect}
                data={data}
                columns={columns}
                loading={loading}
              />
            </Box>
          )}

          {activeStep === 1 && (
            <StepMapping
              columns={columns}
              data={data}
              mapping={state.mapping}
              updateMapping={state.updateMapping}
              modelFields={modelFields}
              validateFile={async () => {
                const res = await validateFile();
                if (res) setActiveStep(2);
              }}
              uploading={state.uploading}
            />
          )}

          {activeStep === 2 && (
            <StepValidation
              validation={state.validation}
              uploading={state.uploading}
              onContinue={async () => {
                if (state.validation?.invalid_rows > 0) return;
                await resolveAddresses();
                setActiveStep(3);
              }}
            />
          )}

          {activeStep === 3 && (
            <StepVerifyAddresses
              resolution={addressResolution}
              overrides={addressOverrides}
              setOverrides={setAddressOverrides}
              uploading={state.uploading}
            />
          )}

          {activeStep === 4 && (
            <StepFinalImport
              validation={state.validation}
              importFile={importFile}
              uploading={state.uploading}
              importResult={state.importResult}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DataImportPage;